import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Pill } from '../ui/pill';
import { GlassyCard } from '../ui/glassy-card';
import { Progress } from '../ui/progress';
import {
  Download,
  FileText,
  Database,
  Archive,
  FolderOpen,
  CheckCircle,
  AlertCircle,
  Loader2,
  File,
  Package,
  HardDrive,
  Share2
} from 'lucide-react';
import { SessionInfo } from '../../stores/storageStore';
import { useTranslation } from '../../locales';
import { useLanguageStore } from '../../stores/languageStore';

interface ExportSessionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: SessionInfo | null;
  onExport: (sessionId: string, format: string, dirHandle?: FileSystemDirectoryHandle) => Promise<void>;
}

type ExportFormat = 'zip' | 'csv' | 'json' | 'jsonl' | 'binary' | 'all';
type ExportStep = 'format' | 'location' | 'nativeLocation' | 'progress' | 'success' | 'error';

interface ExportOption {
  id: ExportFormat;
  name: string;
  description: string;
  icon: React.ReactNode;
  fileExtension: string;
  estimatedSize: string;
  recommended?: boolean;
}

export function ExportSessionModal({ open, onOpenChange, session, onExport }: ExportSessionModalProps) {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('zip');
  const [currentStep, setCurrentStep] = useState<ExportStep>('format');
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [savedLocally, setSavedLocally] = useState(false);

  const exportOptions: ExportOption[] = [
    {
      id: 'zip',
      name: t.exportModal.fmtZipName,
      description: t.exportModal.fmtZipDesc,
      icon: <Package strokeWidth={1.5} className="w-5 h-5" />,
      fileExtension: 'folder',
      estimatedSize: session?.size || '0 B',
      recommended: true
    },
    {
      id: 'csv',
      name: t.exportModal.fmtCsvName,
      description: t.exportModal.fmtCsvDesc,
      icon: <FileText strokeWidth={1.5} className="w-5 h-5" />,
      fileExtension: '.csv',
      estimatedSize: t.exportModal.sizeCompressed50
    },
    {
      id: 'json',
      name: t.exportModal.fmtJsonName,
      description: t.exportModal.fmtJsonDesc,
      icon: <Database strokeWidth={1.5} className="w-5 h-5" />,
      fileExtension: '.json',
      estimatedSize: t.exportModal.sizeOriginal70
    },
    {
      id: 'jsonl',
      name: t.exportModal.fmtJsonlName,
      description: t.exportModal.fmtJsonlDesc,
      icon: <File strokeWidth={1.5} className="w-5 h-5" />,
      fileExtension: '.jsonl',
      estimatedSize: t.exportModal.sizeOriginal65
    },
    {
      id: 'binary',
      name: t.exportModal.fmtBinaryName,
      description: t.exportModal.fmtBinaryDesc,
      icon: <Archive strokeWidth={1.5} className="w-5 h-5" />,
      fileExtension: '.bin',
      estimatedSize: t.exportModal.sizeCompressed30
    },
    {
      id: 'all',
      name: t.exportModal.fmtAllName,
      description: t.exportModal.fmtAllDesc,
      icon: <Download strokeWidth={1.5} className="w-5 h-5" />,
      fileExtension: 'multiple',
      estimatedSize: session?.size || '0 B'
    }
  ];

  const handleFormatSelect = (format: ExportFormat) => {
    setSelectedFormat(format);
  };

  const handleStartExport = async () => {
    if (!session) return;

    // 네이티브(iOS/Android) 환경: 저장 방식(기기 내부 저장 vs 공유 시트) 선택 단계로 이동.
    // 웹 환경: 기존 디렉토리 선택 + Blob 다운로드 경로 유지.
    if (Capacitor.isNativePlatform()) {
      setCurrentStep('nativeLocation');
      return;
    }

    setCurrentStep('location');

    try {
      // 사용자에게 저장 위치 선택 요청 (기본 폴더 제한 없음)
      const dirHandle = await (window as any).showDirectoryPicker({
        mode: 'readwrite'
      });

      setCurrentStep('progress');
      setProgress(0);

      // 진행률 시뮬레이션
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      // 실제 내보내기 실행 (디렉토리 핸들 전달)
      await onExport(session.id, selectedFormat, dirHandle);

      clearInterval(progressInterval);
      setProgress(100);

      setTimeout(() => {
        setCurrentStep('success');
      }, 500);

    } catch (error) {
      console.error('내보내기 실패:', error);
      setErrorMessage(error instanceof Error ? error.message : t.exportModal.unknownError);
      setCurrentStep('error');
    }
  };

  /**
   * 네이티브 공유 경로.
   *
   * MobileShareService는 네이티브 전용 코드(@capacitor/*)를 포함하므로 dynamic import로 로드해
   * 웹 번들에서 분리한다. 모달이 지원하는 포맷(zip/csv/json/jsonl/binary/all)을
   * 서비스가 지원하는 3종(zip/csv/json)으로 정규화한다.
   */
  const handleNativeShare = async () => {
    if (!session) return;

    setSavedLocally(false);
    setCurrentStep('progress');
    setProgress(30);

    try {
      const { MobileShareService } = await import('../../services/MobileShareService');

      // 포맷 정규화: jsonl→json, all/binary→zip (전체 아카이브로 대체)
      const shareFormat =
        selectedFormat === 'csv' ? 'csv'
        : selectedFormat === 'json' || selectedFormat === 'jsonl' ? 'json'
        : 'zip';

      setProgress(70);
      await MobileShareService.shareSession(session.id, shareFormat);

      setProgress(100);
      setTimeout(() => {
        setCurrentStep('success');
      }, 500);
    } catch (error) {
      console.error('네이티브 공유 실패:', error);
      setErrorMessage(error instanceof Error ? error.message : t.exportModal.unknownError);
      setCurrentStep('error');
    }
  };

  /**
   * 네이티브 기기 내부 저장 경로.
   * 공유 시트 대신 @capacitor/filesystem의 Documents 디렉토리에 파일을 직접 저장한다.
   */
  const handleLocalSave = async () => {
    if (!session) return;

    setSavedLocally(true);
    setCurrentStep('progress');
    setProgress(30);

    try {
      const { MobileShareService } = await import('../../services/MobileShareService');

      // 포맷 정규화: jsonl→json, all/binary→zip (전체 아카이브로 대체)
      const shareFormat =
        selectedFormat === 'csv' ? 'csv'
        : selectedFormat === 'json' || selectedFormat === 'jsonl' ? 'json'
        : 'zip';

      setProgress(70);
      await MobileShareService.saveToLocalStorage(session.id, shareFormat);

      setProgress(100);
      setTimeout(() => {
        setCurrentStep('success');
      }, 500);
    } catch (error) {
      console.error('로컬 저장 실패:', error);
      setErrorMessage(error instanceof Error ? error.message : t.exportModal.unknownError);
      setCurrentStep('error');
    }
  };

  const handleClose = () => {
    // 상태 초기화를 지연시켜 부드러운 전환 보장
    setTimeout(() => {
      setCurrentStep('format');
      setProgress(0);
      setErrorMessage('');
      setSelectedFormat('zip');
    }, 200);
    onOpenChange(false);
  };

  const handleRetry = () => {
    setCurrentStep('format');
    setProgress(0);
    setErrorMessage('');
  };

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl mx-2 h-[800px] bg-slate-900 text-white flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-white">
            <Download strokeWidth={1.5} className="w-5 h-5" />
            {t.exportModal.title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-4 overflow-hidden">
          {/* 세션 정보 */}
          <GlassyCard className="p-3 flex-shrink-0">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="font-medium text-white text-sm">{session.name}</h3>
                <div className="flex items-center gap-3 text-xs t-soft mt-1">
                  <span>📅 {session.date}</span>
                  <span>⏰ {session.time}</span>
                  <span>⏱️ {session.duration}</span>
                  <span>📱 {session.deviceName}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="lb-eyebrow text-[10px]">{t.exportModal.fileSize}</div>
                <div className="font-medium text-white text-sm">{session.size}</div>
              </div>
            </div>
            <div className="flex gap-1 mt-2">
              {session.formats.map(format => (
                <Pill size="sm" variant="secondary" key={format} className="text-xs px-2 py-0">
                  {format}
                </Pill>
              ))}
            </div>
          </GlassyCard>

          {/* 단계별 콘텐츠 */}
          {currentStep === 'format' && (
            <>
              <div className="flex-1 flex flex-col overflow-hidden">
                <h3 className="lb-h3 t-strong mb-3 flex-shrink-0">{t.exportModal.formatSelection}</h3>
                <div className="flex-1 overflow-y-auto pr-2 space-y-2">
                  {exportOptions.map((option) => (
                    <GlassyCard
                      key={option.id}
                      className={`p-3 cursor-pointer transition-all hover:shadow-md border-border ${
                        selectedFormat === option.id
                          ? 'ring-1 ring-indigo-400 bg-indigo-500/10 border-indigo-500/40'
                          : 'bg-muted hover:bg-muted'
                      }`}
                      onClick={() => handleFormatSelect(option.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-1.5 rounded-lg ${
                          selectedFormat === option.id
                            ? 'bg-indigo-800 text-indigo-400'
                            : 'bg-white/[0.04] t-soft'
                        }`}>
                          {option.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-white text-sm">{option.name}</h4>
                            {option.recommended && (
                              <Pill size="sm" variant="primary" className="text-xs bg-green-600 text-green-100 px-1.5 py-0">
                                {t.exportModal.recommended}
                              </Pill>
                            )}
                          </div>
                          <p className="text-xs t-soft mt-0.5">{option.description}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs t-soft">
                            <span>📁 {option.fileExtension}</span>
                            <span>📊 {option.estimatedSize}</span>
                          </div>
                        </div>
                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                          selectedFormat === option.id
                            ? 'bg-indigo-500 border-indigo-500/30'
                            : 'border-muted-foreground/20'
                        }`}>
                          {selectedFormat === option.id && (
                            <div className="w-full h-full rounded-full bg-card scale-50"></div>
                          )}
                        </div>
                      </div>
                    </GlassyCard>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 flex-shrink-0 pt-2">
                <Pill variant="secondary" onClick={handleClose}>
                  {t.exportModal.cancel}
                </Pill>
                <Pill variant="primary" onClick={handleStartExport}>
                  <FolderOpen strokeWidth={1.5} className="w-4 h-4 mr-2" />
                  {t.exportModal.selectLocation}
                </Pill>
              </div>
            </>
          )}

          {currentStep === 'nativeLocation' && (
            <div className="flex-1 flex flex-col">
              <h3 className="lb-h3 t-strong mb-3 flex-shrink-0">{t.exportModal.saveMethodTitle}</h3>
              <div className="flex-1 overflow-y-auto pr-2 space-y-2">
                {/* 기기 내부 저장 */}
                <GlassyCard
                  className="p-4 cursor-pointer transition-all hover:shadow-md border-border hover:bg-muted"
                  onClick={handleLocalSave}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-800 text-indigo-400">
                      <HardDrive strokeWidth={1.5} className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white text-sm">{t.exportModal.saveToDevice}</h4>
                      <p className="text-xs t-soft mt-0.5">{t.exportModal.saveToDeviceDesc}</p>
                    </div>
                  </div>
                </GlassyCard>

                {/* 공유 시트로 전송 */}
                <GlassyCard
                  className="p-4 cursor-pointer transition-all hover:shadow-md border-border hover:bg-muted"
                  onClick={handleNativeShare}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-800 text-emerald-400">
                      <Share2 strokeWidth={1.5} className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white text-sm">{t.exportModal.shareToApps}</h4>
                      <p className="text-xs t-soft mt-0.5">{t.exportModal.shareToAppsDesc}</p>
                    </div>
                  </div>
                </GlassyCard>
              </div>

              <div className="flex justify-end gap-2 flex-shrink-0 pt-2">
                <Pill variant="secondary" onClick={() => setCurrentStep('format')}>
                  {t.exportModal.cancel}
                </Pill>
              </div>
            </div>
          )}

          {currentStep === 'location' && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FolderOpen strokeWidth={1.5} className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
                <h3 className="lb-h3 t-strong mb-2">{t.exportModal.locationSelecting}</h3>
                <p className="t-soft">
                  {t.exportModal.locationSelectingDesc}
                </p>
              </div>
            </div>
          )}

          {currentStep === 'progress' && (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-md space-y-6">
                <div className="text-center">
                  <Loader2 strokeWidth={1.5} className="w-12 h-12 text-indigo-400 mx-auto mb-4 animate-spin" />
                  <h3 className="lb-h3 t-strong mb-2">{t.exportModal.progressTitle}</h3>
                  <p className="t-soft text-sm">
                    {selectedFormat === 'zip' ? t.exportModal.progressZip :
                     selectedFormat === 'all' ? t.exportModal.progressAll :
                     t.exportModal.progressFormat(selectedFormat.toUpperCase())}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-white">
                    <span>{t.exportModal.progressLabel}</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              </div>
            </div>
          )}

          {currentStep === 'success' && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <CheckCircle strokeWidth={1.5} className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="lb-h3 t-strong mb-2">{t.exportModal.successTitle}</h3>
                <p className="t-soft mb-6">
                  {savedLocally ? t.exportModal.saveLocalSuccessDesc : t.exportModal.successDesc}
                </p>
                <Pill
                  variant="primary"
                  onClick={handleClose}
                >
                  {t.exportModal.done}
                </Pill>
              </div>
            </div>
          )}

          {currentStep === 'error' && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <AlertCircle strokeWidth={1.5} className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="lb-h3 t-strong mb-2">{t.exportModal.errorTitle}</h3>
                <p className="t-soft mb-6">
                  {errorMessage}
                </p>
                <div className="flex justify-center gap-2">
                  <Pill variant="secondary" onClick={handleClose}>
                    {t.exportModal.cancel}
                  </Pill>
                  <Pill variant="primary" onClick={handleRetry}>
                    {t.exportModal.retry}
                  </Pill>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
